
import ASSETS from '@generio-ai/library-legacy/constants/assets'
import CATEGORY from '@generio-ai/library-legacy/constants/category'
import QUALITY from '@generio-ai/library-legacy/constants/quality'
import VIEWER from '@generio-ai/library-legacy/constants/viewer'
import AUGMENTATION from '@/constants/augmentation'
import BALANCE from '@/constants/balance'
import { TaskFetch } from '@/classes/taskfetch.js'
import { requires } from '@/utils/validate.js'


export default {

    // base fetch (does nothing)
    async fetchBase({}, {
        component,
        category,
    }) {
        // ensure parameters are present
        requires(component, category)

        return new TaskFetch(
            component,
            category,
            async (resolve, reject, progress, app, parentCard) => {
                resolve({ assets: null })
            }
        )
    },

    // make sketch
    async fetchMakeSketch({ dispatch, getters }, {
        component,
        preload = true
    }) {
        // ensure parameters are present
        requires(component, preload)

        return new TaskFetch(
            component,
            CATEGORY.SKETCH,
            async (resolve, reject, progress, app, parentCard) => {
                try {
                    // inform user about status
                    progress("making sketch")

                    // prepare additional
                    const additional = parentCard.createChildAdditional(
                        component, CATEGORY.SKETCH
                    )

                    // get primary card and assets
                    const primaryCard = getters.getPrimaryCardForCard(app, parentCard)
                    const primaryAssets = primaryCard.getSelectedOrAllAssets(1)
                    const primaryAsset = primaryAssets[0]

                    // wait for all assets to be ready
                    await dispatch("awaitReady", {
                        assets: primaryAssets
                    })

                    // call route to make sketch
                    const response = await dispatch('assets/generateImageSketch', {
                        app,
                        id: primaryAsset.id,
                        fileKey: ASSETS.FILES.KEY.DEFAULT,
                        sub: ASSETS.IMAGES.KEY.SKETCH,
                        thumbnail: true,
                        additional
                    }, { root: true })

                    // retrieve sketch asset from response
                    const sketchAssets = [response.assets[0]]

                    // if asset should be preloaded
                    if (preload) {
                        await dispatch("awaitRetrieved", {
                            assets: sketchAssets
                        })
                    }

                    // resolved successfully
                    resolve({ assets: sketchAssets })
                }
                catch (error) {
                    // reject with error
                    reject(error)
                }
            }
        )
    },

    // generate images
    async fetchGenerateImages({ dispatch, getters }, {
        component,
        count = 1,
        prompts = null,
        resolution = 1024,
        seed = -1,
        preload = true
    }) {
        // ensure parameters are present
        requires(component, count, resolution, preload)

        return new TaskFetch(
            component,
            CATEGORY.PREVIEW,
            async (resolve, reject, progress, app, parentCard) => {
                try {
                    // inform user about status
                    if (count > 1) progress("generating previews")
                    else progress("generating preview")

                    // prepare additional
                    const additional = parentCard.createChildAdditional(
                        component, CATEGORY.PREVIEW
                    )

                    // get primary card and assets
                    const primaryCard = getters.getPrimaryCardForCard(app, parentCard)
                    const primaryAssets = primaryCard.getSelectedOrAllAssets(1)
                    const primaryAsset = primaryAssets[0]

                    // wait for all assets to be ready
                    await dispatch("awaitReady", {
                        assets: primaryAssets
                    })

                    // placeholder for action and payload
                    let action = null
                    let payload = null

                    // determine action based on category
                    switch (primaryCard.category) {
                        // generate image from prompt
                        case CATEGORY.PRIMITIVE:
                            action = "assets/generateImageFromPrompt"
                            payload = {
                                app,
                                prompt: parentCard.prompt.system,
                                augmentation: parentCard.custom?.augmentation ?? AUGMENTATION.DEFAULT,
                                resolution,
                                transparencyActive: true,
                                transparencyFillActive: true,
                                transparencyFillMargin: 8,
                                thumbnail: true,
                                seed,
                                additional
                            }
                            break

                        // generate image from image
                        case CATEGORY.PHOTO:
                        case CATEGORY.PREVIEW:
                            action = "assets/generateImageFromAssets"
                            payload = {
                                app,
                                assets: [{
                                    id: primaryAsset.id,
                                    fileKey: ASSETS.FILES.KEY.DEFAULT,
                                    subtype: "photo"
                                }],
                                prompt: parentCard.prompt.system,
                                augmentation: parentCard.custom?.augmentation ?? AUGMENTATION.DEFAULT,
                                resolution,
                                strength: parentCard.custom?.balance ?? BALANCE.DEFAULT,
                                transparencyActive: true,
                                transparencyFillActive: false,
                                thumbnail: true,
                                seed,
                                additional
                            }
                            break

                        // generate image from sketch
                        case CATEGORY.SKETCH:
                            action = "assets/generateImageFromAssets"
                            payload = {
                                app,
                                assets: [{
                                    id: primaryAsset.id,
                                    fileKey: ASSETS.FILES.KEY.DEFAULT,
                                    subtype: "sketch"
                                }],
                                prompt: parentCard.prompt.system,
                                augmentation: parentCard.custom?.augmentation ?? AUGMENTATION.DEFAULT,
                                resolution,
                                strength: parentCard.custom?.balance ?? BALANCE.DEFAULT,
                                transparencyActive: true,
                                transparencyFillActive: false,
                                thumbnail: true,
                                seed,
                                additional
                            }
                            break

                        // generate image from composite
                        case CATEGORY.COMPOSITE:
                            action = "assets/generateImageFromAssets"
                            payload = {
                                app,
                                assets: [
                                    {
                                        id: primaryAsset.id,
                                        fileKey: ASSETS.FILES.KEY.DEFAULT,
                                        subtype: "photo"
                                    },
                                    {
                                        id: primaryAsset.id,
                                        fileKey: ASSETS.FILES.KEY.MARKER,
                                        subtype: "mask"
                                    }
                                ],
                                prompt: parentCard.prompt.system,
                                augmentation: parentCard.custom?.augmentation ?? AUGMENTATION.DEFAULT,
                                resolution,
                                strength: 1.0,
                                transparencyActive: true,
                                transparencyFillActive: false,
                                thumbnail: true,
                                seed,
                                additional
                            }
                            break

                        // generate image from model
                        case CATEGORY.MODEL:
                            // render a depth map first (to control the image generation)
                            await dispatch('assets/renderFile', {
                                app,
                                asset: primaryAsset,
                                fileKey: ASSETS.FILES.KEY.DEFAULT,
                                targetFileKey: ASSETS.FILES.KEY.RENDER,
                                resolution: 1024,
                                parameters: {
                                    animation_active: false,
                                    mode: 'depth'
                                }
                            }, { root: true })

                            // wait for the file to be ready
                            await dispatch("awaitReady", {
                                assets: [primaryAsset],
                                fileKey: ASSETS.FILES.KEY.RENDER
                            })

                            action = "assets/generateImageFromAssets"
                            payload = {
                                app,
                                assets: [{
                                    id: primaryAsset.id,
                                    fileKey: ASSETS.FILES.KEY.RENDER,
                                    subtype: "depth"
                                }],
                                prompt: parentCard.prompt.user,
                                augmentation: parentCard.custom?.augmentation ?? AUGMENTATION.DEFAULT,
                                resolution,
                                strength: 1.0,
                                transparencyActive: true,
                                transparencyFillActive: false,
                                thumbnail: true,
                                seed,
                                additional
                            }
                            break

                        default:
                            throw Error("Unknown asset category: " + primaryCard.category)
                    }

                    // placeholder to collect images
                    const imageAssets = []

                    // generate as many images as specified by count
                    for (let i = 0; i < count; i++) {
                        // determine if we perform prompt overwrite
                        if (prompts && Array.isArray(prompts) && prompts.length === count) {
                            // get specific prompt for this iteration
                            const prompt = prompts[i]

                            // overwrite prompt in payload
                            payload.promptPositive = prompt
                            payload.additional.prompt.system = prompt
                        }

                        const response = await dispatch(action, payload, { root: true })
                        imageAssets.push(response?.assets?.[0])
                    }

                    // if asset should be preloaded
                    if (preload) {
                        await dispatch("awaitRetrieved", {
                            assets: imageAssets
                        })
                    }

                    // resolved successfully
                    resolve({ assets: imageAssets })
                }
                catch (error) {
                    // reject with error
                    reject(error)
                }
            }
        )
    },

    // generate model
    async fetchGenerateModel({ dispatch, getters }, {
        component,
        quality = QUALITY.DEFAULT,
        solid = true,
        texture = false,
        textureMode = 'native',
        textureTransparency = false,
        textureViews = null,
        textureSequence = true,
        variantImageGenerate = 'qwen',
        seed = -1, 
        parameters = {}
    }) {
        // ensure parameters are present
        requires(component, quality, solid, texture)

        return new TaskFetch(
            component,
            CATEGORY.MODEL,
            async (resolve, reject, progress, app, parentCard) => {

                try {
                    // inform user about start of generation
                    progress('starting model generation', VIEWER.STAGE.NOISE)

                    //// VALIDATE QUOTA

                    // fetch current quota
                    const fetch = await dispatch('subscriptions/fetch', null, { root: true })

                    // raise an error if the user has insufficient quota
                    if (fetch?.quota == 0 || fetch?.quota < -1) {
                        const error = new Error('Insufficient quota for 3D model generation')
                        error.status = 402
                        throw error
                    }

                    //// PREPARE ASSETS FOR MODEL GENERATION

                    // determine if dual (images + model) or not (images)
                    const dual = getters.isDualCard(parentCard)

                    // collect all assets (for model generation)
                    const allAssetDicts = []

                    // get image card and assets
                    const imageCard = getters.getCardByCategories(app, parentCard, [CATEGORY.PHOTO, CATEGORY.PREVIEW])
                    const imageAssets = imageCard.getSelectedOrAllAssets()
                    const imageAssetDicts = imageAssets.map(asset => ({ id: asset.id }))

                    // get model card and asset
                    var modelCard = null, modelAsset = null, modelAssetDict = null
                    if (dual) {
                        modelCard = getters.getCardByCategories(app, parentCard, [CATEGORY.MODEL])
                        modelAsset = modelCard.getSelectedOrAllAssets(1)[0]
                        allAssetDicts.push({ id: modelAsset.id })
                    }

                    //// ANALYZING MATERIAL

                    // inform user about material analysis
                    progress(`analyzing material`, VIEWER.STAGE.NOISE)

                    // keep track of the material file key
                    let materialFileKey = null

                    // iterate over all image assets and prepare material file
                    for (const asset of imageAssets) {

                        // get default file transparency
                        const defaultFile = asset.files?.default
                        const transparency = defaultFile?.details?.transparency ?? false

                        // if the default file is transparent, use it as the material file
                        if (transparency) {
                            materialFileKey = ASSETS.FILES.KEY.DEFAULT
                        }
                        // else, segment the default file
                        else {
                            materialFileKey = ASSETS.FILES.KEY.MATERIAL

                            // check if material file is already present
                            if (!asset.files?.[materialFileKey]) {
                                await dispatch('assets/segmentImage', {
                                    app,
                                    id: asset.id,
                                    fileKey: ASSETS.FILES.KEY.DEFAULT,
                                    targetFileKey: ASSETS.FILES.KEY.MATERIAL,
                                    thumbnail: false
                                }, { root: true })
                            }
                        }

                        // add to all asset dicts
                        allAssetDicts.push({
                            id: asset.id,
                            fileKey: materialFileKey,
                            subtype: 'photo', // TODO make constant in library
                            perspective: asset.additional?.perspective
                        })
                    }

                    // wait for all assets to be ready
                    await dispatch("awaitReady", {
                        assets: imageAssetDicts,
                        fileKey: materialFileKey
                    })

                    //// ANALYZING SHAPE

                    // only when no mesh exists yet
                    if (!dual) {

                        // inform user about shape analysis
                        progress('analyzing shape', VIEWER.STAGE.NOISE)

                        // fixed shape file key
                        const shapeFileKey = ASSETS.FILES.KEY.SHAPE

                        // iterate over all image assets and prepare shape file
                        for (const asset of imageAssets) {

                            // check if shape file is already present
                            if (!asset.files?.[shapeFileKey]) {
                                // generate shape file
                                await dispatch('assets/generateImageClay', {
                                    app,
                                    id: asset.id,
                                    fileKey: materialFileKey,
                                    targetFileKey: shapeFileKey,
                                    keepTransparency: true,
                                    thumbnail: false
                                }, { root: true })
                            }

                            // add to all asset dicts
                            allAssetDicts.push({
                                id: asset.id,
                                fileKey: shapeFileKey,
                                subtype: 'clay', // TODO make constant in library
                                perspective: asset.additional?.perspective
                            })
                        }

                        // wait for all assets to be ready
                        await dispatch("awaitReady", {
                            assets: imageAssetDicts,
                            fileKey: shapeFileKey
                        })
                    }

                    // inform user about finished analysis
                    progress('analysis finished', VIEWER.STAGE.ANALYZE)

                    //// GENERATING MODEL

                    // prepare additional
                    const additional = parentCard.createChildAdditional(
                        component, CATEGORY.MODEL
                    )

                    // generate model asset
                    const response = await dispatch('assets/generateModelFromAsset', {
                        app,
                        assets: allAssetDicts,
                        seed,
                        quality,
                        solid,
                        texture,
                        textureMode,
                        textureTransparency,
                        textureViews,
                        textureSequence,
                        variantImageGenerate,
                        parameters,
                        additional,
                        thumbnail: true
                    }, { root: true })

                    // collect generated model assets from response
                    const generatedAsset = response?.assets?.[0]

                    // wait for the generated model to be retrieved
                    await dispatch("awaitRetrievedModel", {
                        modelAsset: generatedAsset,
                        onProgress: progress,
                        onResolve: resolve
                    })
                }
                catch (error) {
                    // probe quota (maybe the trigger of the error)
                    dispatch('subscriptions/probeQuota', {
                        error,
                        callback: () => {
                            // destroy last card
                            dispatch('flows/removeActiveCard', {
                                app: app
                            }, { root: true })
                        }
                    }, { root: true })

                    // reject with error
                    reject(error)
                }
            }
        )
    },

    // optimize model main
    async fetchOptimizeModelMain({ dispatch, getters }, {
        component,
        polygonCount = 50000,
        polygonType = 'triangles',
        keepLargestActive = false,
        keepLargestCount = 1,
        fillHolesActive = false,
        fillHolesLimit = 0.1,
        smoothSurfaceActive = false,
        smoothSurfaceStrength = 0.5,
        clipSideActive = false,
        clipSideAxis = 'bottom',
        clipSidePercentage = 0.1,
        normalMapActive = true,
        occlusionMapActive = false
    }) {
        // ensure parameters are presents
        requires(component)

        return new TaskFetch(
            component,
            CATEGORY.MODEL,
            async (resolve, reject, progress, app, parentCard) => {

                try {
                    // inform user about optimization
                    progress('optimizing structure', VIEWER.STAGE.NOISE)

                    //// VALIDATE QUOTA

                    // fetch current quota
                    const fetch = await dispatch('subscriptions/fetch', null, { root: true })

                    // raise an error if the user has insufficient quota
                    if (fetch?.quota == 0 || fetch?.quota < -1) {
                        const error = new Error('Insufficient quota for 3D model generation')
                        error.status = 402
                        throw error
                    }

                    //// PREPARE OPTIMIZATION

                    // prepare additional
                    const additional = parentCard.createChildAdditional(
                        component, CATEGORY.MODEL
                    )

                    // get primary asset
                    const primaryCard = getters.getCardByCategories(app, parentCard, [CATEGORY.MODEL])
                    const primaryAsset = primaryCard.getSelectedOrAllAssets(1)[0]

                    // wait for all assets to be ready
                    await dispatch("awaitReady", {
                        assets: [primaryAsset]
                    })

                    // prepare parameters
                    const parameters = {
                        "texture_normal": normalMapActive,
                        "texture_occlusion": occlusionMapActive
                    }

                    //// EXECUTE OPTIMIZATION

                    // generate model variant from prompt
                    const response = await dispatch('assets/optimizeModelMain', {
                        app,
                        id: primaryAsset?.id,
                        fileKey: ASSETS.FILES.KEY.DEFAULT,
                        polygonCount,
                        polygonType,
                        keepLargestActive,
                        keepLargestCount,
                        fillHolesActive,
                        fillHolesLimit,
                        smoothSurfaceActive,
                        smoothSurfaceStrength,
                        clipSideActive,
                        clipSideAxis,
                        clipSidePercentage,
                        parameters,
                        additional,
                        thumbnail: true
                    }, { root: true })

                    // collect model assets from response
                    let modelAsset = response.assets[0]

                    // wait for the optimized model to be retrieved
                    await dispatch("awaitRetrievedModel", {
                        modelAsset: modelAsset,
                        onProgress: progress,
                        onResolve: resolve
                    })
                }
                catch (error) {
                    // probe quota (maybe the trigger of the error)
                    dispatch('subscriptions/probeQuota', {
                        error,
                        callback: () => {
                            // destroy last card
                            dispatch('flows/removeActiveCard', {
                                app: app
                            }, { root: true })
                        }
                    }, { root: true })

                    // reject with error
                    reject(error)
                }
            }
        )
    },

    // optimize model watertight
    async fetchOptimizeModelWatertight({ dispatch, getters }, {
        component,
        remeshMethod,
        remeshQuality,
        polygonMaximum,
    }) {
        // ensure parameters are presents
        requires(component)

        return new TaskFetch(
            component,
            CATEGORY.MODEL,
            async (resolve, reject, progress, app, parentCard) => {

                try {
                    // inform user about optimization
                    progress('making model watertight', VIEWER.STAGE.NOISE)

                    //// VALIDATE QUOTA

                    // fetch current quota
                    const fetch = await dispatch('subscriptions/fetch', null, { root: true })

                    // raise an error if the user has insufficient quota
                    if (fetch?.quota == 0 || fetch?.quota < -1) {
                        const error = new Error('Insufficient quota for 3D model generation')
                        error.status = 402
                        throw error
                    }
                    
                    //// PREPARE OPTIMIZATION

                    // prepare additional
                    const additional = parentCard.createChildAdditional(
                        component, CATEGORY.MODEL
                    )

                    // get primary asset
                    const primaryCard = getters.getCardByCategories(app, parentCard, [CATEGORY.MODEL])
                    const primaryAsset = primaryCard.getSelectedOrAllAssets(1)[0]

                    // wait for all assets to be ready
                    await dispatch("awaitReady", {
                        assets: [primaryAsset]
                    })

                    //// EXECUTE OPTIMIZATION

                    // generate model variant from prompt
                    const response = await dispatch('assets/optimizeModelWatertight', {
                        app,
                        id: primaryAsset?.id,
                        fileKey: ASSETS.FILES.KEY.DEFAULT,
                        method: remeshMethod,
                        remeshQuality,
                        polygonMaximum,
                        additional,
                        thumbnail: true
                    }, { root: true })

                    // collect model assets from response
                    let modelAsset = response.assets[0]

                    // wait for the optimized model to be retrieved
                    await dispatch("awaitRetrievedModel", {
                        modelAsset: modelAsset,
                        onProgress: progress,
                        onResolve: resolve
                    })
                }
                catch (error) {
                    // probe quota (maybe the trigger of the error)
                    dispatch('subscriptions/probeQuota', {
                        error,
                        callback: () => {
                            // destroy last card
                            dispatch('flows/removeActiveCard', {
                                app: app
                            }, { root: true })
                        }
                    }, { root: true })

                    // reject with error
                    reject(error)
                }
            }
        )
    }
}